import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { Button } from "../components/ui/Button.js";
import { Input } from "../components/ui/Input.js";
import { Card } from "../components/ui/Card.js";
import { Badge } from "../components/ui/Badge.js";
import { useToast } from "../hooks/use-toast.js";
import { useMutation } from "@apollo/client";
import { ADD_WAITLIST_ENTRY } from "../lib/graphqlOperations.js";
import { Navbar } from "../components/layout/Navbar.js";
import Footer from "../components/layout/Footer.js";
import { useWaitlistCount } from "../hooks/useWaitlistCount.js";
import fruitVendor from "../assets/fruitVendor.png";
import multiVendor from "../assets/stock_images/multiVendor.png";
import logo from "../assets/stock_images/logo.png";
import appMockup from "../assets/quro_app_mockup.png";
import playStoreIconSvg from "../assets/play-store-icon.svg";
import appStoreIconSvg from "../assets/app-store-icon.svg";

import {
  Search,
  ShoppingBag,
  Utensils,
  Wrench,
  Store,
  MapPin,
  Clock,
  Check,
  X,
  Star,
  ChevronRight,
  Sparkles,
  Heart,
  TrendingUp,
  Users,
  ArrowRight,
  Package,
  Truck,
  ShieldCheck,
} from "lucide-react";

const vendors = [
  {
    id: 1,
    name: "Biryani House",
    category: "Indian Cuisine",
    rating: 4.8,
    image: "https://images.unsplash.com/photo-1631515243349-e0cb75fb8d3a?auto=format&fit=crop&w=600&h=800&q=80",
    deliveryTime: "25-35 min",
  },
  {
    id: 2,
    name: "Fresh Farms",
    category: "Organic",
    rating: 4.9,
    image: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=600&h=800&q=80",
    deliveryTime: "15-25 min",
  },
  {
    id: 3,
    name: "Roti Corner",
    category: "North Indian",
    rating: 4.7,
    image: "https://images.unsplash.com/photo-1585937421612-70a008356fbe?auto=format&fit=crop&w=600&h=800&q=80",
    deliveryTime: "20-30 min",
  },
  {
    id: 4,
    name: "Hardware Hub",
    category: "Hardware",
    rating: 4.6,
    image: "https://images.unsplash.com/photo-1530124566582-a618bc2615dc?auto=format&fit=crop&w=600&h=800&q=80",
    deliveryTime: "30-45 min",
  },
  {
    id: 5,
    name: "Baker's Delight",
    category: "Bakery",
    rating: 4.9,
    image: "https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&w=600&h=800&q=80",
    deliveryTime: "20-30 min",
  },
  {
    id: 6,
    name: "Green Grocers",
    category: "Produce",
    rating: 4.8,
    image: "https://images.unsplash.com/photo-1519999482648-25049ddd37b1?auto=format&fit=crop&w=600&h=800&q=80",
    deliveryTime: "15-20 min",
  },
];

const steps = [
  {
    icon: ShoppingBag,
    title: "Browse Products",
    description: "Fresh produce, electric bulbs, buffalo milk, biryani - if it's available locally or globally, we've got it.",
  },
  {
    icon: Store,
    title: "Select Your Vendor",
    description: "Choose exactly which local vendor you want to source from - your favorite shops, your choice.",
  },
  {
    icon: Truck,
    title: "Get It Delivered",
    description: "Fresh products delivered to your doorstep in under 10 minutes. No dark stores, just real vendors.",
  },
];

const comparisonData = [
  { feature: "Real vendor prices", quro: true, others: false },
  { feature: "No surge pricing", quro: true, others: false },
  { feature: "Multi-vendor orders", quro: true, others: false },
  { feature: "Fresh from source", quro: true, others: false },
  { feature: "Worry-free returns", quro: true, others: false },
  { feature: "Support local business", quro: true, others: false },
];

const categories = [
  { icon: Utensils, label: "Food" },
  { icon: ShoppingBag, label: "Groceries" },
  { icon: Wrench, label: "Hardware" },
  { icon: Package, label: "Boutique" },
  { icon: Heart, label: "Pharmacy" },
  { icon: Sparkles, label: "& More" },
];

export default function Home() {
  const { counts } = useWaitlistCount();
  // console.log(counts);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const { toast } = useToast();

  const [addWaitlistEntry, { loading: joinWaitlistLoading }] = useMutation(ADD_WAITLIST_ENTRY, {
    onCompleted: (data) => {
      if (data?.addWaitlistEntry?.success) {
        toast({
          title: "Welcome to Quro!",
          description: "You're on the list for 1 year of free deliveries.",
        });
        setName("");
        setPhone("");
      } else {
        const message = data?.addWaitlistEntry?.message || "Something went wrong. Please try again.";
        toast({
          title: "Something went wrong",
          description: message,
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      toast({
        title: "Something went wrong",
        description: error.message || "Please enter a valid name and phone number.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name && phone) {
      const cleanPhone = phone.replace(/\s+/g, "").replace(/\D/g, "").replace(/^(\+?91|0)/, "").slice(-10);
      if (cleanPhone.length !== 10) {
        toast({
          title: "Invalid Phone",
          description: "Please enter a valid 10-digit phone number.",
          variant: "destructive",
        });
        return;
      }
      addWaitlistEntry({ variables: { name, phone: cleanPhone, entryType: "customer" } });
    }
  };

  const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.quro.app&pcampaignid=web_share";
  const APP_STORE_URL = "https://apps.apple.com/in/app/quro-today/id6759046388";

  const PlayStoreIcon = () => (
    <img src={playStoreIconSvg} alt="" aria-hidden="true" className="w-8 h-8" />
  );

  const AppStoreIcon = () => (
    <img src={appStoreIconSvg} alt="" aria-hidden="true" className="w-8 h-8" />
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* Animated Background Blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px]"
        />
        <motion.div
          animate={{
            x: [0, -80, 0],
            y: [0, 100, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute top-[20%] -right-[5%] w-[35%] h-[35%] bg-accent/10 rounded-full blur-[120px]"
        />
      </div>

      <div className="relative z-10">
        <section id="waitlist" className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-8 relative">
              {/* Floating Decorative Icons */}
              <motion.div
                animate={{ y: [0, -15, 0], rotate: [0, 10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-12 -left-8 text-primary/20 hidden md:block"
              >
                <Package className="w-16 h-16" />
              </motion.div>
              <motion.div
                animate={{ y: [0, 15, 0], rotate: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute top-1/2 -right-12 text-accent/20 hidden md:block"
              >
                <Utensils className="w-16 h-16" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 backdrop-blur-md text-primary text-sm font-bold border border-primary/20"
              >
                <Sparkles className="w-4 h-4 animate-pulse" />
                <span>One Year Free Deliveries • Limited Time Offer</span>
              </motion.div>

              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
                className="font-display text-5xl sm:text-6xl md:text-8xl font-bold leading-[0.9] tracking-tighter"
              >
                Skip the Dark Store.{" "}
                <span className="text-primary relative inline-block">
                  Fresh Neighbors
                  <svg className="absolute -bottom-4 left-0 w-full" viewBox="0 0 358 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <motion.path
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 1.5, delay: 0.8, ease: "easeInOut" }}
                      d="M3 9C118.5 3 239.5 3 355 9"
                      stroke="currentColor"
                      strokeWidth="8"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </motion.h1>

              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-xl"
              >
                Everything available locally or globally—delivered. Fresh produce, electric bulbs, buffalo milk from farmers. 
                <span className="text-foreground font-semibold"> One app, one quick delivery.</span>
              </motion.p>

              <motion.form 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                onSubmit={handleSubmit} 
                className="flex flex-col gap-5 max-w-md relative p-2 rounded-3xl bg-card/30 backdrop-blur-2xl border border-white/20 shadow-xl"
              >
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 relative">
                      <Input
                        type="text"
                        placeholder="Your Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="h-14 rounded-2xl border-white/20 bg-white/50 backdrop-blur-sm focus-visible:ring-primary/50 shadow-inner px-5 text-lg"
                      />
                    </div>
                    <div className="flex-1 relative">
                      <Input
                        type="tel"
                        placeholder="Phone Number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="h-14 rounded-2xl border-white/20 bg-white/50 backdrop-blur-sm focus-visible:ring-primary/50 shadow-inner px-5 text-lg"
                      />
                    </div>
                  </div>
                  <Button
                    id="join-waitlist-submit"
                    type="submit"
                    size="lg"
                    disabled={joinWaitlistLoading || !name || !phone}
                    className="h-16 rounded-2xl text-xl font-bold bg-primary hover:bg-primary/90 text-white shadow-glow shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-1 transition-all duration-300"
                  >
                    {joinWaitlistLoading ? "Joining..." : "Get Free Delivery"}
                    <ArrowRight className="w-6 h-6 ml-3" />
                  </Button>
                </div>
              </motion.form>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <span className="text-sm font-bold text-muted-foreground uppercase tracking-wider">
                  Or download now
                </span>
                <div className="flex flex-wrap gap-3">
                  <motion.a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-3 px-5 py-3 bg-zinc-950 text-white rounded-2xl border border-white/10 hover:bg-black transition-all shadow-xl shadow-black/30"
                    aria-label="Get Quro on Google Play"
                  >
                    <PlayStoreIcon />
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase font-bold opacity-60 leading-none tracking-wider">Get it on</span>
                      <span className="text-base font-bold leading-tight">Google Play</span>
                    </div>
                  </motion.a>
                  <motion.a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-3 px-5 py-3 bg-zinc-950 text-white rounded-2xl border border-white/10 hover:bg-black transition-all shadow-xl shadow-black/30"
                    aria-label="Download Quro on the App Store"
                  >
                    <AppStoreIcon />
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase font-bold opacity-60 leading-none tracking-wider">Download on</span>
                      <span className="text-base font-bold leading-tight">App Store</span>
                    </div>
                  </motion.a>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1 }}
                className="flex flex-wrap items-center gap-8 text-sm text-foreground/70 font-medium pt-4"
              >
                <div className="flex items-center gap-3 group">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <span>Join <span className="text-primary font-bold">{counts.customer + counts.vendor}+</span> Users</span>
                </div>
                <div className="flex items-center gap-3 group">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Clock className="w-5 h-5 text-accent" />
                  </div>
                  <span>10 Min Delivery</span>
                </div>
                <div className="flex items-center gap-3 group">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span>Quality Promise</span>
                </div>
              </motion.div>
            </div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, x: 50 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
              className="relative"
            >
              <div className="relative rounded-[3rem] overflow-hidden shadow-2xl border border-white/20 group">
                <img
                  src={fruitVendor}
                  alt="Fresh local produce and groceries"
                  className="w-full h-[550px] lg:h-[700px] object-cover transition-transform duration-1000 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60" />
                
                {/* Floating Tags over Image */}
                <motion.div 
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 1.2 }}
                  className="absolute top-10 right-10 flex flex-col gap-3 items-end"
                >
                  <Badge variant="secondary" className="bg-white/10 hover:bg-white/20 text-white backdrop-blur-xl border border-white/20 text-base py-2 px-5 rounded-2xl shadow-xl">
                    <Store className="w-5 h-5 mr-3 text-primary" />
                    Real Local Vendors
                  </Badge>
                  <Badge variant="secondary" className="bg-white/10 hover:bg-white/20 text-white backdrop-blur-xl border border-white/20 text-base py-2 px-5 rounded-2xl shadow-xl">
                    <ShieldCheck className="w-5 h-5 mr-3 text-primary" />
                    Farmer Direct
                  </Badge>
                </motion.div>

                <div className="absolute bottom-10 left-10 right-10">
                  <motion.div 
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.4 }}
                    className="p-8 rounded-[2rem] bg-white/95 backdrop-blur-2xl border border-white/20 shadow-2xl"
                  >
                    <div className="flex items-center gap-5">
                      <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-soft p-3">
                        <img src={logo} alt="Quro Logo" className="w-full h-full object-contain" />
                      </div>
                      <div>
                        <h3 className="text-slate-900 text-2xl font-bold font-display tracking-tight leading-none mb-1">Quro Excellence</h3>
                        <p className="text-slate-500 font-medium text-sm">Connecting you to your roots through technology.</p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Decorative blobs behind image */}
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-primary/30 rounded-full blur-[100px] pointer-events-none -z-10" />
              <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-accent/30 rounded-full blur-[100px] pointer-events-none -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              How Quro Works
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Three simple steps to get everything you need from your neighborhood, delivered in under 10 minutes.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="relative p-10 rounded-[2.5rem] bg-card/40 backdrop-blur-lg border border-border/50 shadow-soft hover:-translate-y-3 transition-all duration-500 overflow-hidden group"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full -mr-8 -mt-8 transition-transform duration-500 group-hover:scale-125" />
                <div className="relative w-20 h-20 rounded-3xl bg-primary/10 flex items-center justify-center mb-8 shadow-inner group-hover:bg-primary group-hover:text-white transition-colors duration-500">
                  <step.icon className="w-10 h-10 transition-transform group-hover:scale-110" />
                </div>
                <div className="absolute top-10 right-10 text-6xl font-display font-black text-muted-foreground/10 select-none group-hover:text-primary/10 transition-colors">
                  0{index + 1}
                </div>
                <h3 className="relative font-display text-2xl font-bold mb-4 tracking-tight">{step.title}</h3>
                <p className="relative text-muted-foreground text-lg leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vendor Showcase Section */}
      <section id="vendors" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-4"
            >
              <h2 className="font-display text-4xl md:text-6xl font-bold leading-tight tracking-tight">
                Featured Local Vendors
              </h2>
              <p className="text-muted-foreground text-xl leading-relaxed max-w-xl">
                From your favorite biryani spot to the freshest produce stand — all your neighborhood favorites, curated for you.
              </p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative h-[250px] lg:h-[300px] rounded-[3rem] overflow-hidden shadow-2xl border border-border/20 group hidden lg:block"
            >
               <img src={vendors[0].image} alt="Featured Vendor" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
               <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />
               <div className="absolute bottom-8 left-8 right-8">
                  <div className="bg-white/95 backdrop-blur-2xl p-6 rounded-2xl border border-white/20 shadow-xl">
                     <div className="flex items-center gap-4 text-slate-900">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                           <Store className="w-7 h-7 text-primary" />
                        </div>
                        <div>
                           <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-0.5">Community Network</p>
                           <span className="font-display font-black text-lg tracking-tight leading-none">Partnering with India's Finest 10,000+ Shops</span>
                        </div>
                     </div>
                  </div>
               </div>
            </motion.div>
          </div>
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="flex gap-6 overflow-x-auto pb-4 px-4 sm:px-6 lg:px-8 snap-x snap-mandatory scrollbar-hide">
            {vendors.map((vendor, index) => (
              <motion.div
                key={vendor.id}
                initial={{ opacity: 0, scale: 0.9, x: 20 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="relative flex-shrink-0 w-[280px] sm:w-[320px] lg:w-[380px] rounded-[2.5rem] overflow-hidden group snap-start border border-border/40 shadow-soft cursor-pointer bg-card/30 backdrop-blur-md"
              >
                <div className="aspect-[4/5] overflow-hidden relative">
                  <img
                    src={vendor.image}
                    alt={vendor.name}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                  />
                  {/* Glass Overlay on Hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent z-10 opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="absolute inset-x-0 bottom-0 p-8 z-20 text-white flex flex-col justify-end translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                    <div className="flex items-center gap-3 mb-4">
                      <Badge className="bg-white/10 hover:bg-white/20 text-white backdrop-blur-xl border border-white/20 font-bold px-4 py-1.5 rounded-xl uppercase tracking-wider text-[10px]">
                        {vendor.category}
                      </Badge>
                      <div className="flex items-center gap-1.5 text-sm bg-black/40 px-3 py-1 rounded-full backdrop-blur-md border border-white/10">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-bold">{vendor.rating}</span>
                      </div>
                    </div>
                    <h3 className="font-display font-bold text-3xl mb-2 tracking-tight">{vendor.name}</h3>
                    <div className="flex items-center gap-2 text-white/80 font-medium">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                        <Clock className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-lg">{vendor.deliveryTime}</span>
                    </div>
                    
                    <motion.div 
                      className="mt-6 flex items-center text-primary font-bold overflow-hidden"
                      initial={{ width: 0 }}
                      whileHover={{ width: "auto" }}
                    >
                      <span className="whitespace-nowrap transition-all duration-300 opacity-0 group-hover:opacity-100">Browse Menu</span>
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Multi-Vendor Magic Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
              className="relative order-2 lg:order-1 flex justify-center lg:justify-start"
            >
              <div className="relative w-full max-w-lg aspect-square">
                <div className="absolute inset-2 md:inset-8 border-2 border-primary/20 rounded-[3rem] animate-[ping_10s_infinite_linear]" />
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 items-center justify-center blur-3xl rounded-full opacity-50" />
                <div className="relative rounded-[3rem] overflow-hidden shadow-2xl border-4 border-white/50 group w-full h-full">
                  <img
                    src={multiVendor}
                    alt="Multiple dishes from different vendors"
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                  
                {/* Floating Notification-like Cards */}
                <motion.div 
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute top-10 -left-10 md:-left-16 p-4 rounded-2xl bg-white/95 backdrop-blur-xl shadow-xl border border-white/20 flex items-center gap-3 z-40"
                >
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                    <Utensils className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Ordering from</p>
                    <p className="font-display font-black text-sm text-slate-900">Biryani House</p>
                  </div>
                </motion.div>

                <motion.div 
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  className="absolute bottom-10 -right-10 md:-right-16 p-4 rounded-2xl bg-white/95 backdrop-blur-xl shadow-xl border border-white/20 flex items-center gap-3 z-40"
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <ShoppingBag className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Plus Grocery</p>
                    <p className="font-display font-black text-sm text-slate-900">Green Grocers</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8 order-1 lg:order-2 lg:pl-8"
            >
              <div className="space-y-4">
                <Badge className="bg-primary/10 text-primary border-none text-xs font-bold uppercase tracking-wider px-3 py-1">Exclusive Feature</Badge>
                <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
                  Multi-Vendor Magic
                </h2>
              </div>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Why settle for just one shop? Mix and match your favorites—order <span className="text-foreground font-semibold">Biryani House</span>, fresh rotis, and your daily groceries in a single checkout.
              </p>
              <div className="grid gap-5">
                {[
                  { text: "We handle all vendor coordination for you", desc: "Automated logistics ensures fresh arrival." },
                  { text: "Single checkout, single delivery fee", desc: "No more paying multiple service charges." },
                  { text: "Quick commerce speed, local quality", desc: "10-minute delivery from real shops." },
                  { text: "Worry-free returns & instant support", desc: "Transparent policies with human help." },
                ].map((item, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * index }}
                    className="group"
                  >
                    <div className="flex items-start gap-5 p-5 rounded-[2rem] bg-background/50 backdrop-blur-xl border border-border/40 hover:border-primary/50 transition-all shadow-sm hover:shadow-glow hover:shadow-primary/5">
                      <div className="w-12 h-12 rounded-[1rem] bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-all transform group-hover:rotate-6">
                        <Check className="w-7 h-7" />
                      </div>
                      <div className="space-y-1">
                        <p className="font-display font-bold text-xl tracking-tight leading-tight">{item.text}</p>
                        <p className="text-muted-foreground text-sm font-medium">{item.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              <Button size="lg" className="h-14 px-8 rounded-2xl text-lg shadow-xl shadow-primary/20 group hover:shadow-primary/40">
                Try Multi-Vendor Order
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Transparent Pricing Section */}
      <section id="pricing" className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              Transparent Pricing Promise
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              No surge pricing. No hidden fees. No arbitrary markups.
              What vendors charge is what you pay - always.
            </p>
          </div>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="absolute inset-0 bg-primary/20 blur-[100px] -z-10 opacity-30" />
            <Card className="overflow-hidden border-border/50 shadow-soft backdrop-blur-2xl bg-card/60 rounded-[3rem]">
              <div className="grid grid-cols-3 bg-primary/5 p-8 border-b border-border/50">
                <div className="text-lg font-bold">Benefit</div>
                <div className="text-center flex flex-col items-center">
                  <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center mb-3 shadow-soft">
                    <img src={logo} alt="Quro" className="w-8 h-8" />
                  </div>
                  <span className="text-primary font-black text-xl tracking-tighter">Quro</span>
                </div>
                <div className="text-center text-muted-foreground flex flex-col items-center pt-2">
                  <div className="w-10 h-10 rounded-2xl bg-muted flex items-center justify-center mb-4">
                    <X className="w-6 h-6" />
                  </div>
                  <span className="font-bold text-sm uppercase tracking-wider">Others</span>
                </div>
              </div>
              {comparisonData.map((row, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.01, backgroundColor: "hsl(var(--primary) / 0.08)" }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  className={`grid grid-cols-3 p-8 items-center relative group ${
                    index % 2 === 0 ? 'bg-transparent' : 'bg-primary/5'
                  } ${index !== comparisonData.length - 1 ? 'border-b border-border/30' : ''}`}
                >
                  <div className="text-lg font-bold text-foreground/80 tracking-tight group-hover:text-primary transition-colors">
                    {row.feature}
                  </div>
                  
                  <div className="flex justify-center">
                    <motion.div 
                      initial={{ scale: 0.8, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center border-2 transition-all duration-500 ${
                        row.quro 
                          ? "bg-emerald-500/10 border-emerald-500/20 shadow-[0_0_15px_rgba(16,185,129,0.1)] group-hover:shadow-[0_0_25px_rgba(16,185,129,0.2)]" 
                          : "bg-destructive/10 border-destructive/20"
                      }`}
                    >
                      {row.quro ? (
                        <Check className="w-6 h-6 text-emerald-600" />
                      ) : (
                        <X className="w-6 h-6 text-destructive" />
                      )}
                    </motion.div>
                  </div>

                  <div className="flex justify-center">
                    <motion.div 
                      initial={{ scale: 0.8, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      transition={{ delay: index * 0.05 + 0.1 }}
                      className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-all duration-500 ${
                        row.others 
                          ? "bg-emerald-500/5 border-emerald-500/10" 
                          : "bg-destructive/5 border-destructive/10 opacity-40 group-hover:opacity-60"
                      }`}
                    >
                      {row.others ? (
                        <Check className="w-5 h-5 text-emerald-600/70" />
                      ) : (
                        <X className="w-5 h-5 text-destructive/70" />
                      )}
                    </motion.div>
                  </div>

                  {/* Hover Highlight Bar */}
                  <motion.div 
                    initial={{ opacity: 0, x: -10 }}
                    whileHover={{ opacity: 1, x: 0 }}
                    className="absolute left-0 top-1/4 bottom-1/4 w-1 bg-primary rounded-r-full"
                  />
                </motion.div>
              ))}
            </Card>
          </motion.div>
          <div className="mt-12 flex flex-wrap justify-center gap-6">
            <Badge className="bg-white/50 backdrop-blur-md text-foreground border-border/50 px-6 py-3 rounded-2xl text-base font-bold shadow-soft">
              <ShieldCheck className="w-5 h-5 mr-3 text-primary" />
              Price Match Guarantee
            </Badge>
            <Badge className="bg-white/50 backdrop-blur-md text-foreground border-border/50 px-6 py-3 rounded-2xl text-base font-bold shadow-soft">
              <TrendingUp className="w-5 h-5 mr-3 text-primary" />
              Real-time Vendor Prices
            </Badge>
          </div>
        </div>
      </section>

      {/* Free Delivery Offer Section */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
              Limited Time Offer
            </Badge>
            <h2 className="font-display text-3xl md:text-5xl font-bold">
              Join Now: 1 Year of Free Deliveries
            </h2>
            <p className="text-lg md:text-xl text-primary-foreground/80 max-w-2xl mx-auto">
              Be among the first to experience Quro. Sign up today and get unlimited free deliveries
              for a full year on everything - fresh produce, food, hardware, buffalo milk, and more. One app for all your needs.
            </p>
            <div className="flex flex-wrap justify-center gap-6 py-4">
              {categories.map((cat, index) => (
                <div key={index} className="flex flex-col items-center gap-2">
                  <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center">
                    <cat.icon className="w-7 h-7" />
                  </div>
                  <span className="text-sm font-medium">{cat.label}</span>
                </div>
              ))}
            </div>
            <form onSubmit={handleSubmit} className="flex flex-col gap-3 max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/60"
                />
                <Input
                  type="tel"
                  placeholder="Phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/60"
                />
              </div>
              <Button
                type="submit"
                variant="secondary"
                size="lg"
                disabled={joinWaitlistLoading || !name || !phone}
              >
                {joinWaitlistLoading ? "Joining..." : "Claim Free Year"}
              </Button>
            </form>
            <p className="text-sm text-primary-foreground/60">
              No credit card required. We'll notify you when Quro launches in your area.
            </p>
          </div>
        </div>
      </section>
      {/* Download App Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-transparent to-accent/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-card/40 backdrop-blur-3xl border border-white/20 rounded-[3rem] p-8 md:p-16 shadow-2xl overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <Badge className="bg-primary/20 text-primary border-none">Available Now</Badge>
                <h2 className="font-display text-4xl md:text-6xl font-bold leading-tight tracking-tighter">
                  Bring Your Neighborhood <br />
                  <span className="text-primary">To Your Pocket</span>
                </h2>
                <p className="text-xl text-muted-foreground leading-relaxed max-w-lg">
                  Get the best of your local shops, farmers, and restaurants delivered in under 10 minutes. 
                  Experience the magic of multi-vendor ordering today.
                </p>
                <div className="flex flex-wrap gap-4 pt-4">
                  <motion.a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-4 px-8 py-4 bg-zinc-950 text-white rounded-2xl border border-white/10 hover:bg-black transition-all shadow-2xl shadow-black/40 group overflow-hidden relative"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <PlayStoreIcon />
                    <div className="flex flex-col">
                      <span className="text-[12px] uppercase font-bold opacity-60 leading-none tracking-wider">Get it on</span>
                      <span className="text-xl font-bold leading-tight">Google Play</span>
                    </div>
                  </motion.a>

                  <motion.a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center gap-4 px-8 py-4 bg-zinc-950 text-white rounded-2xl border border-white/10 hover:bg-black transition-all shadow-2xl shadow-black/40 group overflow-hidden relative"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <AppStoreIcon />
                    <div className="flex flex-col">
                      <span className="text-[12px] uppercase font-bold opacity-60 leading-none tracking-wider">Download on the</span>
                      <span className="text-xl font-bold leading-tight">App Store</span>
                    </div>
                  </motion.a>

                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 blur-[120px] rounded-full animate-pulse" />
                <div className="relative z-10 bg-zinc-900/10 backdrop-blur-3xl border border-white/10 rounded-[3.5rem] p-4 rotate-3 hover:rotate-0 transition-all duration-700 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] group-hover:scale-105">
                  <div className="aspect-[9/19] w-full max-w-[320px] mx-auto bg-zinc-950 rounded-[2.8rem] border-[8px] border-zinc-900/80 overflow-hidden relative shadow-2xl">
                    <div className="absolute top-0 inset-x-0 h-7 bg-zinc-900/80 z-20 flex justify-center items-center">
                      <div className="w-20 h-5 bg-zinc-950 rounded-full" />
                    </div>
                    <img 
                      src={appMockup} 
                      alt="Quro App Interface" 
                      className="w-full h-full object-cover relative z-10"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/40 to-transparent z-15" />
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      </div>
    </div>
  );
}
